package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.events.Event
import kotlin.math.PI
import kotlin.math.min
import kotlin.math.sin

private const val HIGH_SCORE_KEY = "fitrahRunnerHighScore"

// Colors
private const val TEAL = "#0E7C7B"
private const val TEAL_LIGHT = "#14A3A0"
private const val GOLD = "#C8962E"
private const val GOLD_DARK = "#9A7220"
private const val OBSTACLE_DARK = "#922B21"

// Game constants
private const val CANVAS_HEIGHT = 250.0
private const val GROUND_Y = 220.0
private const val GRAVITY = 0.55
private const val JUMP_VELOCITY = -10.0
private const val CHILD_X = 70.0
private const val CHILD_WIDTH = 45.0
private const val CHILD_HEIGHT = 45.0
private const val OBSTACLE_WIDTH = 44.0
private const val OBSTACLE_HEIGHT = 38.0
private const val BASE_SPEED = 2.8
private const val SPEED_INCREMENT = 0.08
private const val MAX_SPEED = 5.5
private const val MIN_OBSTACLE_SPACING = 260.0
private const val MAX_OBSTACLE_SPACING = 380.0
private const val COLLISION_SHRINK = 18.0 // shrink collision box for forgiveness

// Lottie camel div size
private const val CAMEL_DIV_SIZE = 55

private enum class RunnerState { READY, PLAYING, GAME_OVER }

private const val SANTA_FILE = "/animations/obstacles/santa.json"

private class Obstacle(
    var x: Double,
    val label: String,
    val lottieFile: String,
    var scored: Boolean = false,
    var div: HTMLDivElement? = null,
    var lottieInstance: dynamic = null,
    var jumpPhase: Double = 0.0
)

private val OBSTACLE_LABELS = listOf(
    "Bad Words", "Violence", "Scary Scene", "Rude Behavior",
    "Lying", "Bullying", "Greed", "Jealousy",
    "Cheating", "Backbiting", "Anger", "Arrogance"
)

private val OBSTACLE_LOTTIE_FILES = listOf(
    "/animations/obstacles/angry.json",
    "/animations/obstacles/cursing.json",
    "/animations/obstacles/fire.json",
    "/animations/obstacles/santa.json",
    "/animations/obstacles/gift.json"
)

@Composable
fun FitrahRunnerGame() {
    Div({
        style {
            width(100.percent)
            position(Position.Relative)
            property("user-select", "none")
            property("-webkit-user-select", "none")
            property("-webkit-tap-highlight-color", "transparent")
            cursor("pointer")
        }
        attr("id", "fitrah-runner-wrapper")
    }) {
        DisposableEffect(Unit) {
            val wrapper = document.getElementById("fitrah-runner-wrapper") ?: return@DisposableEffect onDispose {}

            // Ensure wrapper has relative positioning for absolute child positioning
            (wrapper as HTMLDivElement).style.position = "relative"
            wrapper.style.setProperty("overflow", "hidden")

            val canvas = document.createElement("canvas") as HTMLCanvasElement
            canvas.id = "fitrah-runner-canvas"
            canvas.style.width = "100%"
            canvas.style.height = "${CANVAS_HEIGHT.toInt()}px"
            canvas.style.display = "block"
            canvas.style.borderRadius = "12px"
            wrapper.appendChild(canvas)

            // Create camel Lottie container div
            val camelDiv = document.createElement("div") as HTMLDivElement
            camelDiv.style.position = "absolute"
            camelDiv.style.width = "${CAMEL_DIV_SIZE}px"
            camelDiv.style.height = "${CAMEL_DIV_SIZE}px"
            camelDiv.style.setProperty("pointer-events", "none")
            camelDiv.style.zIndex = "1"
            // No flip needed - JSON is pre-flipped
            camelDiv.style.left = "${CHILD_X.toInt()}px"
            camelDiv.style.top = "${(GROUND_Y - CHILD_HEIGHT).toInt()}px"
            wrapper.appendChild(camelDiv)

            // Track Lottie animation instance
            var lottieAnim: dynamic = null
            var lottieLoaded = false

            // Load lottie-web via CDN if not already present
            val existingScript = document.querySelector("script[src*='lottie-web']")
            fun initLottie() {
                val lottie = js("window.lottie")
                if (lottie != null && lottie != js("undefined")) {
                    val options = js("{}")
                    options.container = camelDiv
                    options.renderer = "svg"
                    options.loop = true
                    options.autoplay = false
                    options.path = "/animations/baby_camel.json"
                    lottieAnim = lottie.loadAnimation(options)
                    lottieLoaded = true
                }
            }

            if (existingScript != null) {
                // lottie-web already loaded
                initLottie()
            } else {
                val script = document.createElement("script")
                script.asDynamic().src = "https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js"
                script.asDynamic().onload = { _: Event ->
                    initLottie()
                }
                document.head?.appendChild(script)
            }

            // Game state
            var state = RunnerState.READY
            var childY = GROUND_Y - CHILD_HEIGHT
            var velocityY = 0.0
            var isOnGround = true
            var hasDoubleJumped = false
            var score = 0
            var highScore = localStorage.getItem(HIGH_SCORE_KEY)?.toIntOrNull() ?: 0
            var obstacles = mutableListOf<Obstacle>()
            var speed = BASE_SPEED
            var canvasWidth = 400.0
            var animFrameId = 0
            var lastTime = 0.0
            var gameOverTimer = 0.0
            var distanceTraveled = 0.0
            var groundOffset = 0.0
            var bgOffset = 0.0
            var runFrame = 0.0
            var lottieWasPlaying = false

            fun getCanvasWidth(): Double {
                return canvas.getBoundingClientRect().width
            }

            fun randomSpacing(): Double {
                return MIN_OBSTACLE_SPACING + (MAX_OBSTACLE_SPACING - MIN_OBSTACLE_SPACING) * js("Math.random()").unsafeCast<Double>()
            }

            fun randomLabel(): String {
                val idx = (js("Math.random()").unsafeCast<Double>() * OBSTACLE_LABELS.size).toInt()
                return OBSTACLE_LABELS[idx.coerceIn(0, OBSTACLE_LABELS.size - 1)]
            }

            fun randomLottieFile(): String {
                val idx = (js("Math.random()").unsafeCast<Double>() * OBSTACLE_LOTTIE_FILES.size).toInt()
                return OBSTACLE_LOTTIE_FILES[idx.coerceIn(0, OBSTACLE_LOTTIE_FILES.size - 1)]
            }

            fun createObstacleDiv(obs: Obstacle) {
                val div = document.createElement("div") as HTMLDivElement
                div.style.position = "absolute"
                div.style.width = "${OBSTACLE_WIDTH.toInt()}px"
                div.style.height = "${OBSTACLE_HEIGHT.toInt()}px"
                div.style.setProperty("pointer-events", "none")
                div.style.zIndex = "1"
                div.style.left = "${obs.x.toInt()}px"
                div.style.top = "${(GROUND_Y - OBSTACLE_HEIGHT + 10).toInt()}px"

                // Lottie container (sized to obstacle hitbox)
                val lottieContainer = document.createElement("div") as HTMLDivElement
                lottieContainer.style.width = "${OBSTACLE_WIDTH.toInt()}px"
                lottieContainer.style.height = "${OBSTACLE_HEIGHT.toInt()}px"
                div.appendChild(lottieContainer)

                wrapper.appendChild(div)
                obs.div = div

                // Load Lottie animation for this obstacle
                val lottie = js("window.lottie")
                if (lottie != null && lottie != js("undefined")) {
                    val options = js("{}")
                    options.container = lottieContainer
                    options.renderer = "svg"
                    options.loop = true
                    options.autoplay = true
                    options.path = obs.lottieFile
                    obs.lottieInstance = lottie.loadAnimation(options)
                }
            }

            fun updateObstacleDiv(obs: Obstacle) {
                obs.div?.style?.left = "${obs.x.toInt()}px"
                // Santa jumps periodically
                if (obs.lottieFile == SANTA_FILE) {
                    val cycle = obs.jumpPhase % 2.5
                    val jumpOffset = if (cycle < 0.6) {
                        kotlin.math.sin(cycle / 0.6 * kotlin.math.PI) * OBSTACLE_HEIGHT * 1.2
                    } else 0.0
                    obs.div?.style?.top = "${(GROUND_Y - OBSTACLE_HEIGHT - jumpOffset).toInt()}px"
                }
            }

            fun removeObstacleDiv(obs: Obstacle) {
                val anim = obs.lottieInstance
                if (anim != null) {
                    anim.destroy()
                    obs.lottieInstance = null
                }
                obs.div?.parentElement?.removeChild(obs.div!!)
                obs.div = null
            }

            fun removeAllObstacleDivs() {
                for (obs in obstacles) {
                    removeObstacleDiv(obs)
                }
            }

            fun updateCamelPosition(y: Double) {
                // Position the camel div to align with the character hitbox
                // Center the Lottie div on the character's hitbox area
                val leftOffset = CHILD_X + (CHILD_WIDTH - CAMEL_DIV_SIZE) / 2.0
                val topOffset = y + (CHILD_HEIGHT - CAMEL_DIV_SIZE) + 10
                camelDiv.style.left = "${leftOffset}px"
                camelDiv.style.top = "${topOffset}px"

                // Rotation based on velocity: lean back on jump, level at peak, lean forward on descent
                // velocityY is negative going up, 0 at peak, positive coming down
                // Map to: -15deg (lean back) -> 0deg (level) -> +10deg (lean forward)
                val maxRotation = 15.0
                val rotation = if (isOnGround) {
                    0.0
                } else {
                    val normalized = (velocityY / JUMP_VELOCITY).coerceIn(-1.5, 1.5)
                    // Going up (velocityY negative, normalized positive): lean back (negative rotation)
                    // Coming down (velocityY positive, normalized negative): lean forward (positive rotation)
                    -normalized * maxRotation
                }
                camelDiv.style.transform = "rotate(${rotation}deg)"
            }

            fun updateLottiePlayback(currentState: RunnerState, onGround: Boolean) {
                if (!lottieLoaded || lottieAnim == null) return

                when (currentState) {
                    RunnerState.PLAYING -> {
                        if (!lottieWasPlaying) {
                            lottieAnim.play()
                            lottieWasPlaying = true
                        }
                        // Speed up animation when airborne
                        lottieAnim.setSpeed(if (onGround) 1.0 else 2.5)
                    }
                    RunnerState.READY, RunnerState.GAME_OVER -> {
                        if (lottieWasPlaying) {
                            lottieAnim.pause()
                            lottieWasPlaying = false
                        }
                    }
                }
            }

            fun resetGame() {
                canvasWidth = getCanvasWidth()
                childY = GROUND_Y - CHILD_HEIGHT
                velocityY = 0.0
                isOnGround = true
                score = 0
                speed = BASE_SPEED
                distanceTraveled = 0.0
                groundOffset = 0.0
                bgOffset = 0.0
                gameOverTimer = 0.0
                runFrame = 0.0

                // Remove all existing obstacle divs
                removeAllObstacleDivs()
                obstacles.clear()

                // Seed initial obstacles — start far away to give player time
                var x = canvasWidth + 200.0
                for (i in 0 until 3) {
                    val obs = Obstacle(x, randomLabel(), randomLottieFile())
                    obstacles.add(obs)
                    createObstacleDiv(obs)
                    x += randomSpacing()
                }

                updateCamelPosition(childY)
            }

            fun drawBackground(ctx: CanvasRenderingContext2D, cw: Double) {
                // Sky gradient
                val skyGrad = ctx.createLinearGradient(0.0, 0.0, 0.0, GROUND_Y)
                skyGrad.addColorStop(0.0, "#E8F4F0")  // light mint at top
                skyGrad.addColorStop(0.5, "#D4EDE5")  // soft teal
                skyGrad.addColorStop(1.0, "#F5E6C8")  // warm sand near ground
                ctx.fillStyle = skyGrad
                ctx.fillRect(0.0, 0.0, cw, GROUND_Y)

                // Sun
                ctx.fillStyle = "#F0C75E"
                ctx.globalAlpha = 0.6
                ctx.beginPath()
                ctx.arc(cw - 60.0, 40.0, 25.0, 0.0, 2 * PI)
                ctx.fill()
                ctx.globalAlpha = 0.3
                ctx.beginPath()
                ctx.arc(cw - 60.0, 40.0, 35.0, 0.0, 2 * PI)
                ctx.fill()
                ctx.globalAlpha = 1.0

                // Far dunes (slowest parallax - 20% of ground speed)
                val farOffset = bgOffset * 0.2
                ctx.fillStyle = "rgba(200, 180, 140, 0.3)"
                val farDuneWidth = 300.0
                var fx = -(farOffset % farDuneWidth)
                while (fx < cw + farDuneWidth) {
                    ctx.beginPath()
                    ctx.moveTo(fx, GROUND_Y)
                    ctx.quadraticCurveTo(fx + farDuneWidth * 0.5, GROUND_Y - 60.0, fx + farDuneWidth, GROUND_Y)
                    ctx.fill()
                    fx += farDuneWidth
                }

                // Mid dunes (40% of ground speed)
                val midOffset = bgOffset * 0.4
                ctx.fillStyle = "rgba(210, 190, 150, 0.35)"
                val midDuneWidth = 200.0
                var mx = -(midOffset % midDuneWidth) - 80.0
                while (mx < cw + midDuneWidth) {
                    ctx.beginPath()
                    ctx.moveTo(mx, GROUND_Y)
                    ctx.quadraticCurveTo(mx + midDuneWidth * 0.5, GROUND_Y - 35.0, mx + midDuneWidth, GROUND_Y)
                    ctx.fill()
                    mx += midDuneWidth
                }

                // Distant buildings/mosques silhouettes (30% speed)
                val buildingOffset = bgOffset * 0.3
                ctx.fillStyle = "rgba(14, 124, 123, 0.12)"
                val buildingSpacing = 400.0
                var bx = -(buildingOffset % buildingSpacing) + 50.0
                while (bx < cw + buildingSpacing) {
                    // Dome
                    ctx.beginPath()
                    ctx.arc(bx, GROUND_Y - 45.0, 18.0, PI, 0.0)
                    ctx.fillRect(bx - 18.0, GROUND_Y - 45.0, 36.0, 45.0)
                    ctx.fill()
                    // Minaret
                    ctx.fillRect(bx + 30.0, GROUND_Y - 65.0, 6.0, 65.0)
                    // Small crescent on top
                    ctx.beginPath()
                    ctx.arc(bx + 33.0, GROUND_Y - 70.0, 4.0, 0.0, 2 * PI)
                    ctx.fill()
                    bx += buildingSpacing
                }

                // Sandy ground fill below ground line
                ctx.fillStyle = "#EDE0C8"
                ctx.fillRect(0.0, GROUND_Y, cw, CANVAS_HEIGHT - GROUND_Y)
            }

            fun drawStars(ctx: CanvasRenderingContext2D, cw: Double, time: Double) {
                val starPositions = listOf(
                    0.08 to 0.08, 0.2 to 0.15, 0.32 to 0.06, 0.45 to 0.2,
                    0.58 to 0.04, 0.68 to 0.18, 0.78 to 0.09, 0.88 to 0.22,
                    0.14 to 0.28, 0.52 to 0.3, 0.92 to 0.12, 0.38 to 0.25
                )
                for ((i, pos) in starPositions.withIndex()) {
                    val (xFrac, yFrac) = pos
                    val twinkle = 0.15 + 0.15 * sin(time / 600.0 + i * 1.3)
                    ctx.fillStyle = "rgba(200, 150, 46, $twinkle)"
                    val size = 1.2 + (i % 3) * 0.6
                    ctx.beginPath()
                    ctx.arc(xFrac * cw, yFrac * CANVAS_HEIGHT, size, 0.0, 2 * PI)
                    ctx.fill()
                }
            }

            fun drawGround(ctx: CanvasRenderingContext2D, cw: Double) {
                // Main ground line
                ctx.strokeStyle = TEAL
                ctx.lineWidth = 2.0
                ctx.beginPath()
                ctx.moveTo(0.0, GROUND_Y)
                ctx.lineTo(cw, GROUND_Y)
                ctx.stroke()

                // Small decorative dashes along the ground
                ctx.strokeStyle = "rgba(14, 124, 123, 0.3)"
                ctx.lineWidth = 1.0
                var gx = -groundOffset % 40.0
                while (gx < cw) {
                    ctx.beginPath()
                    ctx.moveTo(gx, GROUND_Y + 6)
                    ctx.lineTo(gx + 12, GROUND_Y + 6)
                    ctx.stroke()
                    gx += 40.0
                }

                // Small tufts / dots
                ctx.fillStyle = "rgba(14, 124, 123, 0.15)"
                var tx = -groundOffset % 80.0 + 20.0
                while (tx < cw) {
                    ctx.beginPath()
                    ctx.arc(tx, GROUND_Y + 10, 2.0, 0.0, 2 * PI)
                    ctx.fill()
                    tx += 80.0
                }
            }

            fun drawText(
                ctx: CanvasRenderingContext2D,
                text: String,
                x: Double,
                y: Double,
                size: Int,
                color: String = TEAL,
                align: String = "center"
            ) {
                ctx.font = "bold ${size}px 'Segoe UI', system-ui, sans-serif"
                ctx.asDynamic().textAlign = align
                ctx.asDynamic().textBaseline = "middle"
                ctx.shadowColor = "rgba(0,0,0,0.15)"
                ctx.shadowBlur = 4.0
                ctx.shadowOffsetX = 1.0
                ctx.shadowOffsetY = 1.0
                ctx.fillStyle = color
                ctx.fillText(text, x, y)
                ctx.shadowBlur = 0.0
                ctx.shadowOffsetX = 0.0
                ctx.shadowOffsetY = 0.0
            }

            fun render(ctx: CanvasRenderingContext2D, cw: Double, timestamp: Double) {
                val dpr = window.devicePixelRatio
                canvas.width = (cw * dpr).toInt()
                canvas.height = (CANVAS_HEIGHT * dpr).toInt()
                ctx.scale(dpr, dpr)

                ctx.clearRect(0.0, 0.0, cw, CANVAS_HEIGHT)

                // Background (parallax desert)
                drawBackground(ctx, cw)

                // Stars
                drawStars(ctx, cw, timestamp)

                // Ground
                drawGround(ctx, cw)

                // Update obstacle Lottie div positions
                for (obs in obstacles) {
                    updateObstacleDiv(obs)
                }

                // Character is rendered by the Lottie div overlay, not drawn on canvas
                // Update Lottie div position to match character physics
                updateCamelPosition(childY)
                updateLottiePlayback(state, isOnGround)

                // Score during play
                if (state == RunnerState.PLAYING) {
                    drawText(ctx, "$score", cw / 2, 28.0, 28, GOLD)
                }

                // Ready screen
                if (state == RunnerState.READY) {
                    ctx.fillStyle = "rgba(255, 255, 255, 0.6)"
                    ctx.fillRect(0.0, 0.0, cw, CANVAS_HEIGHT)

                    drawText(ctx, "Stay on the Fitrah", cw / 2, CANVAS_HEIGHT / 2 - 50, 22, TEAL)
                    drawText(ctx, "Jump over bad influences!", cw / 2, CANVAS_HEIGHT / 2 - 22, 13, TEAL_LIGHT)
                    drawText(ctx, "Tap to Start", cw / 2, CANVAS_HEIGHT / 2 + 12, 18, GOLD)

                    if (highScore > 0) {
                        drawText(ctx, "Best: $highScore", cw / 2, CANVAS_HEIGHT / 2 + 42, 14, TEAL_LIGHT)
                    }

                    // Position camel on the ground in ready state
                    updateCamelPosition(GROUND_Y - CHILD_HEIGHT)
                }

                // Game over screen
                if (state == RunnerState.GAME_OVER) {
                    ctx.fillStyle = "rgba(255, 255, 255, 0.7)"
                    ctx.fillRect(0.0, 0.0, cw, CANVAS_HEIGHT)

                    drawText(ctx, "Game Over", cw / 2, CANVAS_HEIGHT / 2 - 45, 26, TEAL)
                    drawText(ctx, "Score: $score", cw / 2, CANVAS_HEIGHT / 2 - 10, 22, GOLD)
                    drawText(ctx, "Best: $highScore", cw / 2, CANVAS_HEIGHT / 2 + 22, 16, TEAL_LIGHT)
                    drawText(ctx, "Tap to Retry", cw / 2, CANVAS_HEIGHT / 2 + 55, 16, GOLD_DARK)
                }
            }

            fun gameLoop(timestamp: Double) {
                val cw = getCanvasWidth()
                if (cw <= 0) {
                    animFrameId = window.requestAnimationFrame { gameLoop(it) }
                    return
                }

                val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D
                if (ctx == null) {
                    animFrameId = window.requestAnimationFrame { gameLoop(it) }
                    return
                }

                val dt = if (lastTime == 0.0) 16.0 else min(timestamp - lastTime, 33.0)
                lastTime = timestamp
                val dtFactor = dt / 16.0

                if (state == RunnerState.PLAYING) {
                    // Gravity — only apply when not on ground
                    velocityY += GRAVITY * dtFactor
                    childY += velocityY * dtFactor

                    // Land on ground
                    if (childY >= GROUND_Y - CHILD_HEIGHT) {
                        childY = GROUND_Y - CHILD_HEIGHT
                        velocityY = 0.0
                        isOnGround = true
                    } else {
                        isOnGround = false
                    }

                    // Running animation frame counter
                    runFrame += dtFactor

                    // Move obstacles
                    val currentSpeed = speed * dtFactor
                    groundOffset += currentSpeed
                    bgOffset += currentSpeed

                    for (obs in obstacles) {
                        obs.x -= currentSpeed
                        if (obs.lottieFile == SANTA_FILE) {
                            obs.jumpPhase += dtFactor / 60.0 // normalize to ~seconds
                        }
                    }

                    // Remove off-screen obstacles and add new ones
                    val toRemove = obstacles.filter { it.x + OBSTACLE_WIDTH < -10 }
                    for (obs in toRemove) {
                        removeObstacleDiv(obs)
                    }
                    obstacles.removeAll { it.x + OBSTACLE_WIDTH < -10 }

                    val lastObsX = obstacles.maxOfOrNull { it.x } ?: 0.0
                    if (lastObsX < cw + 50) {
                        val newObs = Obstacle(lastObsX + randomSpacing(), randomLabel(), randomLottieFile())
                        obstacles.add(newObs)
                        createObstacleDiv(newObs)
                    }

                    // Score — distance based
                    distanceTraveled += currentSpeed
                    score = (distanceTraveled / 10.0).toInt()

                    // Speed up gradually
                    speed = min(BASE_SPEED + score * SPEED_INCREMENT * 0.1, MAX_SPEED)

                    // Collision detection — forgiving boxes (same invisible hitbox)
                    val childLeft = CHILD_X + COLLISION_SHRINK / 2
                    val childRight = CHILD_X + CHILD_WIDTH - COLLISION_SHRINK / 2
                    val childTop = childY + COLLISION_SHRINK / 2
                    val childBottom = childY + CHILD_HEIGHT

                    for (obs in obstacles) {
                        val obsLeft = obs.x + COLLISION_SHRINK / 2
                        val obsRight = obs.x + OBSTACLE_WIDTH - COLLISION_SHRINK / 2
                        val santaJump = if (obs.lottieFile == SANTA_FILE) {
                            val cycle = obs.jumpPhase % 2.5
                            if (cycle < 0.6) kotlin.math.sin(cycle / 0.6 * kotlin.math.PI) * OBSTACLE_HEIGHT * 1.2 else 0.0
                        } else 0.0
                        val obsTop = GROUND_Y - OBSTACLE_HEIGHT - santaJump + COLLISION_SHRINK / 2
                        val obsBottom = GROUND_Y - santaJump

                        if (childRight > obsLeft && childLeft < obsRight &&
                            childBottom > obsTop && childTop < obsBottom) {
                            state = RunnerState.GAME_OVER
                        }
                    }

                    if (state == RunnerState.GAME_OVER) {
                        if (score > highScore) {
                            highScore = score
                            localStorage.setItem(HIGH_SCORE_KEY, highScore.toString())
                        }
                        gameOverTimer = 0.0
                    }
                }

                if (state == RunnerState.GAME_OVER) {
                    gameOverTimer += dt
                }

                render(ctx, cw, timestamp)
                animFrameId = window.requestAnimationFrame { gameLoop(it) }
            }

            fun handleInput(@Suppress("UNUSED_PARAMETER") e: Event) {
                when (state) {
                    RunnerState.READY -> {
                        resetGame()
                        state = RunnerState.PLAYING
                    }
                    RunnerState.PLAYING -> {
                        if (isOnGround) {
                            velocityY = JUMP_VELOCITY
                            isOnGround = false
                            hasDoubleJumped = false
                        } else if (!hasDoubleJumped) {
                            velocityY = JUMP_VELOCITY * 0.85
                            hasDoubleJumped = true
                        }
                    }
                    RunnerState.GAME_OVER -> {
                        if (gameOverTimer > 400) {
                            resetGame()
                            state = RunnerState.PLAYING
                        }
                    }
                }
            }

            val touchHandler: (Event) -> Unit = { e ->
                e.preventDefault()
                handleInput(e)
            }
            val clickHandler: (Event) -> Unit = { e -> handleInput(e) }

            canvas.addEventListener("click", clickHandler)
            canvas.addEventListener("touchstart", touchHandler)

            resetGame()
            state = RunnerState.READY
            animFrameId = window.requestAnimationFrame { gameLoop(it) }

            onDispose {
                window.cancelAnimationFrame(animFrameId)
                canvas.removeEventListener("click", clickHandler)
                canvas.removeEventListener("touchstart", touchHandler)
                // Destroy camel Lottie animation
                if (lottieLoaded && lottieAnim != null) {
                    lottieAnim.destroy()
                }
                // Destroy all obstacle Lottie divs
                removeAllObstacleDivs()
                camelDiv.parentElement?.removeChild(camelDiv)
                canvas.parentElement?.removeChild(canvas)
            }
        }
    }
}
